import React from "react";
import { Tooltip } from "@narmi/design_system";
import { Link, matchRoutes, useInRouterContext } from "react-router-dom";
import styles from "./Navigation.module.css";
import { ROUTES } from "@pages/routes";

export function NavigationAnchor({
  to,
  className,
  children,
  ...props
}: React.PropsWithChildren<{
  to: string;
  className?: string;
}> &
  React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const originSupported = useInRouterContext();
  const destinationSupported = matchRoutes(ROUTES, to) !== null;

  // check if both the origin and destination support client-side routing.
  return originSupported && destinationSupported ? (
    <Link {...props} to={to} className={className}>
      {children}
    </Link>
  ) : (
    <a {...props} href={to} className={className}>
      {children}
    </a>
  );
}

export function NavigationLink({
  to,
  icon,
  children,
  compact = false,
  highlighted = false,
}: React.PropsWithChildren<{
  to: string;
  /** Keep this link's own label and icon when it is a section's only visible child. */
  preserveIdentity?: boolean;
  icon?: React.ReactNode;
  compact?: boolean;
  highlighted?: boolean;
}>) {
  // check if both the origin and destination support client-side routing.
  const linkElement = (
    <NavigationAnchor
      to={to}
      className={`${styles.navigationLink} ${highlighted ? styles.highlighted : ""}`}
    >
      {icon}
      {!compact && (
        <div className={`${styles.navigationLinkText} whiteSpace--truncate`}>{children}</div>
      )}
    </NavigationAnchor>
  );

  // only show the tooltip if the link is compact, which means the link text is hidden.
  if (!compact) {
    return linkElement;
  }

  return (
    // @ts-expect-error usage predates strict type checking for string `text`
    <Tooltip text={children} side="right">
      {linkElement}
    </Tooltip>
  );
}
